import type { FunctionComponent } from 'react';

import ScrubBar from '../ScrubBar';

import S from './style.scss';

export type ChapterT = {
  /** Accent color (first modality) for the marker border. */
  borderColor: string | undefined;

  /** Stable key for the chapter (index + video url). */
  id: string;

  /** Start position within the whole session, a [0,1] fraction. */
  start: number;

  thumbnailUrl: string;
  title: string;
};

type PropsT = {
  activeIdx: number;
  chapters: ChapterT[];

  /** Seek to an absolute [0,1] fraction of the whole session. */
  onSeek: (fraction: number) => void;

  /** Jump to the start of the chapter at `idx`. */
  onSeekChapter: (idx: number) => void;

  /** Current session progress, a [0,1] fraction. */
  progress: number;

  /** Whether to render the chapter thumbnails (hidden when maximized). */
  showMarkers: boolean;
};

/**
 * The session scrubber: a draggable progress bar (with a tick at each chapter
 * boundary) and, below it, each chapter's thumbnail positioned at its start.
 * The active chapter's thumbnail is highlighted and shows its title.
 */
const ChapterTimeline: FunctionComponent<PropsT> = ({
  activeIdx,
  chapters,
  onSeek,
  onSeekChapter,
  progress,
  showMarkers,
}) => {
  const ticks = chapters.slice(1).map((c) => c.start);

  return (
    <div className={S.container}>
      <ScrubBar onSeek={onSeek} progress={progress} ticks={ticks} />

      {showMarkers ? (
        <div className={S.markers}>
          {chapters.map((c, i) => {
            const active = i === activeIdx;
            // Cap the active marker at the gap before the next chapter's
            // thumbnail (or the bar end), so its title truncates instead of
            // overrunning the next thumbnail.
            const nextStart = chapters[i + 1]?.start ?? 1;
            return (
              <button
                aria-label={c.title || `Chapter ${i + 1}`}
                className={active ? `${S.marker} ${S.markerActive}` : S.marker}
                key={c.id}
                onClick={() => {
                  onSeekChapter(i);
                }}
                style={{
                  left: `${c.start * 100}%`,
                  maxWidth: active
                    ? `${(nextStart - c.start) * 100}%`
                    : undefined,
                }}
                type="button"
              >
                <img
                  alt=""
                  className={active ? `${S.thumb} ${S.thumbActive}` : S.thumb}
                  src={c.thumbnailUrl}
                  style={{ borderColor: c.borderColor }}
                />
                {active ? (
                  <span className={S.markerTitle}>
                    {c.title}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default ChapterTimeline;
